import React, { useState, useEffect, Suspense } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { setupAxiosInterceptors } from './axiosConfig';
import { LogoFull } from './components/Logo';
import LoginPage from './pages/LoginPage';
import VMListPage from './pages/VMListPage';
import VMDetailPage from './pages/VMDetailPage';
import ConsolePage from './pages/ConsolePage';
import MaintenancePage from './pages/MaintenancePage';

// Admin pages — lazy loaded para não incluir no bundle principal
const LogsPage = React.lazy(() => import('./pages/LogsPage'));
const FirewallPage = React.lazy(() => import('./pages/FirewallPage'));
const ServerPage = React.lazy(() => import('./pages/ServerPage'));
const ClusterPage = React.lazy(() => import('./pages/ClusterPage'));
const ClientsPage = React.lazy(() => import('./pages/ClientsPage'));
const FinanceiroPage = React.lazy(() => import('./pages/FinanceiroPage'));
const UsuariosPage = React.lazy(() => import('./pages/UsuariosPage'));
const MeusUsuariosPage = React.lazy(() => import('./pages/MeusUsuariosPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const NotificacoesPage = React.lazy(() => import('./pages/NotificacoesPage'));
const SolicitacoesPage = React.lazy(() => import('./pages/SolicitacoesPage'));
const TelegramPage = React.lazy(() => import('./pages/TelegramPage'));
const BackupsPage = React.lazy(() => import('./pages/BackupsPage'));
const VmIncidentsPage = React.lazy(() => import('./pages/VmIncidentsPage'));
const EventsPage = React.lazy(() => import('./pages/EventsPage'));

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState('dark');
  const [maintenance, setMaintenance] = useState(null); // { message, isNetworkError }
  const [isLogAdmin, setIsLogAdmin] = useState(false);
  const [isFirewallAdmin, setIsFirewallAdmin] = useState(false);
  const [isRevenda, setIsRevenda] = useState(false);
  const [isRoot, setIsRoot] = useState(false);
  const [authUser, setAuthUser] = useState(null);        // username bruto (não exibir diretamente)
  const [displayUser, setDisplayUser] = useState(null);  // nome de exibição normalizado pelo backend
  const [portalUser, setPortalUser] = useState(null);    // dados do portal_user (id, username, role, displayName)
  const [isSubUser, setIsSubUser] = useState(false);
  const [subUserPermission, setSubUserPermission] = useState(null);
  const [nodeAlerts, setNodeAlerts] = useState(null);

  useEffect(() => {
    setupAxiosInterceptors();

    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);

    const handleMaintenance = (e) => setMaintenance(e.detail);
    window.addEventListener('app:maintenance', handleMaintenance);

    // Verificar sessão ativa via cookie — servidor é a fonte de verdade
    axios.get('/api/auth/me')
      .then(res => {
        // Sincronizar expiresAt do servidor para o countdown local
        if (res.data.expiresAt) {
          localStorage.setItem('sessionExpiry', String(res.data.expiresAt));
        }
        localStorage.setItem('isAuthenticated', 'true');
        setIsAuthenticated(true);
      })
      .catch(() => {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('sessionExpiry');
        setIsAuthenticated(false);
      })
      .finally(() => setLoading(false));

    return () => window.removeEventListener('app:maintenance', handleMaintenance);
  }, []);

  // Reavalia permissões sempre que o estado de autenticação muda
  useEffect(() => {
    if (isAuthenticated) {
      axios.get('/api/admin/me')
        .then(res => {
          setIsLogAdmin(res.data.isLogAdmin);
          setIsFirewallAdmin(res.data.isFirewallAdmin);
          setIsRevenda(res.data.isRevenda || false);
          setIsRoot(res.data.isRoot);
          setAuthUser(res.data.user);
          setDisplayUser(res.data.displayUser);
          setPortalUser(res.data.portalUser || null);
          setIsSubUser(res.data.isSubUser || false);
          setSubUserPermission(res.data.subUserPermission || null);
        })
        .catch(() => { setIsLogAdmin(false); setIsFirewallAdmin(false); setIsRevenda(false); setIsRoot(false); setAuthUser(null); setDisplayUser(null); setPortalUser(null); setIsSubUser(false); setSubUserPermission(null); });
    } else {
      setIsLogAdmin(false);
      setIsFirewallAdmin(false);
      setIsRevenda(false);
      setIsRoot(false);
      setDisplayUser(null);
      setPortalUser(null);
      setIsSubUser(false);
      setSubUserPermission(null);
    }
  }, [isAuthenticated]);

  // Polling de alertas dos nós — DESATIVADO (syslog do Proxmox causa lentidão acumulada)
  // A coleta será reativada via abordagem alternativa (SSH/cron local)
  // useEffect(() => { ... }, [isAuthenticated, isLogAdmin, isFirewallAdmin]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('theme', next);
    document.documentElement.setAttribute('data-theme', next);
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  if (maintenance) {
    return <MaintenancePage message={maintenance.message} isNetworkError={maintenance.isNetworkError} />;
  }

  return (
    <Router>
      <div className="app">
        {isAuthenticated && <NavBar setIsAuthenticated={setIsAuthenticated} theme={theme} toggleTheme={toggleTheme} isLogAdmin={isLogAdmin} setIsLogAdmin={setIsLogAdmin} isFirewallAdmin={isFirewallAdmin} setIsFirewallAdmin={setIsFirewallAdmin} isRevenda={isRevenda} setIsRevenda={setIsRevenda} authUser={authUser} setAuthUser={setAuthUser} displayUser={displayUser} setDisplayUser={setDisplayUser} nodeAlerts={nodeAlerts} />}
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? <VMListPage isAdmin={isLogAdmin || isFirewallAdmin} /> : <LoginPage setIsAuthenticated={setIsAuthenticated} />
            }
          />
          <Route
            path="/vm/:vmid/console/full"
            element={
              isAuthenticated ? <ConsolePage /> : <LoginPage setIsAuthenticated={setIsAuthenticated} />
            }
          />
          <Route
            path="/vm/:vmid/:section"
            element={
              isAuthenticated ? <VMDetailPage /> : <LoginPage setIsAuthenticated={setIsAuthenticated} />
            }
          />
          <Route
            path="/vm/:vmid"
            element={
              isAuthenticated ? <VMDetailPage /> : <LoginPage setIsAuthenticated={setIsAuthenticated} />
            }
          />
          <Route
            path="/logs"
            element={
              !isAuthenticated
                ? <LoginPage setIsAuthenticated={setIsAuthenticated} />
                : isLogAdmin
                  ? <Suspense fallback={<div className="loading">Carregando...</div>}><LogsPage isRoot={isRoot} /></Suspense>
                  : <Navigate to="/" replace />
            }
          />
          <Route
            path="/firewall"
            element={
              !isAuthenticated
                ? <LoginPage setIsAuthenticated={setIsAuthenticated} />
                : isFirewallAdmin
                  ? <Suspense fallback={<div className="loading">Carregando...</div>}><FirewallPage isRoot={isRoot} /></Suspense>
                  : <Navigate to="/" replace />
            }
          />
          <Route
            path="/server"
            element={
              !isAuthenticated
                ? <LoginPage setIsAuthenticated={setIsAuthenticated} />
                : (isLogAdmin || isFirewallAdmin)
                  ? <Suspense fallback={<div className="loading">Carregando...</div>}><ServerPage /></Suspense>
                  : <Navigate to="/" replace />
            }
          />
          <Route
            path="/cluster"
            element={
              !isAuthenticated
                ? <LoginPage setIsAuthenticated={setIsAuthenticated} />
                : (isLogAdmin || isFirewallAdmin)
                  ? <Suspense fallback={<div className="loading">Carregando...</div>}><ClusterPage isAdmin nodeAlerts={nodeAlerts} /></Suspense>
                  : <Navigate to="/" replace />
            }
          />
          <Route
            path="/backups"
            element={
              !isAuthenticated
                ? <LoginPage setIsAuthenticated={setIsAuthenticated} />
                : (isLogAdmin || isFirewallAdmin)
                  ? <Suspense fallback={<div className="loading">Carregando...</div>}><BackupsPage /></Suspense>
                  : <Navigate to="/" replace />
            }
          />
          <Route
            path="/incidentes"
            element={
              !isAuthenticated
                ? <LoginPage setIsAuthenticated={setIsAuthenticated} />
                : (isLogAdmin || isFirewallAdmin)
                  ? <Suspense fallback={<div className="loading">Carregando...</div>}><VmIncidentsPage /></Suspense>
                  : <Navigate to="/" replace />
            }
          />
          <Route
            path="/eventos"
            element={
              !isAuthenticated
                ? <LoginPage setIsAuthenticated={setIsAuthenticated} />
                : (isLogAdmin || isFirewallAdmin)
                  ? <Suspense fallback={<div className="loading">Carregando...</div>}><EventsPage /></Suspense>
                  : <Navigate to="/" replace />
            }
          />
          <Route
            path="/clients"
            element={
              !isAuthenticated
                ? <LoginPage setIsAuthenticated={setIsAuthenticated} />
                : (isLogAdmin || isFirewallAdmin || isRevenda)
                  ? <Suspense fallback={<div className="loading">Carregando...</div>}><ClientsPage readOnly={isRevenda} /></Suspense>
                  : <Navigate to="/" replace />
            }
          />
          <Route
            path="/financeiro"
            element={
              !isAuthenticated
                ? <LoginPage setIsAuthenticated={setIsAuthenticated} />
                : (isLogAdmin || isFirewallAdmin || isRevenda)
                  ? <Suspense fallback={<div className="loading">Carregando...</div>}><FinanceiroPage /></Suspense>
                  : <Navigate to="/" replace />
            }
          />
          <Route
            path="/usuarios"
            element={
              !isAuthenticated
                ? <LoginPage setIsAuthenticated={setIsAuthenticated} />
                : (isLogAdmin || isFirewallAdmin)
                  ? <Suspense fallback={<div className="loading">Carregando...</div>}><UsuariosPage isRoot={isRoot} /></Suspense>
                  : <Navigate to="/" replace />
            }
          />
          <Route
            path="/meus-usuarios"
            element={
              !isAuthenticated
                ? <LoginPage setIsAuthenticated={setIsAuthenticated} />
                : (isRevenda && !isSubUser)
                  ? <Suspense fallback={<div className="loading">Carregando...</div>}><MeusUsuariosPage /></Suspense>
                  : <Navigate to="/" replace />
            }
          />
          <Route
            path="/profile"
            element={
              isAuthenticated
                ? <Suspense fallback={<div className="loading">Carregando...</div>}><ProfilePage /></Suspense>
                : <LoginPage setIsAuthenticated={setIsAuthenticated} />
            }
          />
          <Route
            path="/notificacoes"
            element={
              !isAuthenticated
                ? <LoginPage setIsAuthenticated={setIsAuthenticated} />
                : (isLogAdmin || isFirewallAdmin)
                  ? <Suspense fallback={<div className="loading">Carregando...</div>}><NotificacoesPage /></Suspense>
                  : <Navigate to="/" replace />
            }
          />
          <Route
            path="/solicitacoes"
            element={
              !isAuthenticated
                ? <LoginPage setIsAuthenticated={setIsAuthenticated} />
                : (isLogAdmin || isFirewallAdmin)
                  ? <Suspense fallback={<div className="loading">Carregando...</div>}><SolicitacoesPage /></Suspense>
                  : <Navigate to="/" replace />
            }
          />
          <Route
            path="/telegram"
            element={
              !isAuthenticated
                ? <LoginPage setIsAuthenticated={setIsAuthenticated} />
                : (isLogAdmin || isFirewallAdmin)
                  ? <Suspense fallback={<div className="loading">Carregando...</div>}><TelegramPage /></Suspense>
                  : <Navigate to="/" replace />
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

function formatSessionTime(secs) {
  if (secs === null || secs < 0) return '--:--';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function NavBar({ setIsAuthenticated, theme, toggleTheme, isLogAdmin, setIsLogAdmin, isFirewallAdmin, setIsFirewallAdmin, isRevenda, setIsRevenda, authUser, setAuthUser, displayUser, setDisplayUser, nodeAlerts }) {
  const [vmStats, setVmStats]         = React.useState(null);
  const [menuOpen, setMenuOpen]       = React.useState(false);
  const [gestaoOpen, setGestaoOpen]   = React.useState(false);
  const [totpStatus, setTotpStatus]   = React.useState(null);
  const [totpSetupData, setTotpSetupData] = React.useState(null);
  const [totpConfirm, setTotpConfirm] = React.useState('');
  const [totpMsg, setTotpMsg]         = React.useState('');
  const [totpSaving, setTotpSaving]   = React.useState(false);
  const [sessionLeft, setSessionLeft] = React.useState(null); // segundos restantes
  // Troca de senha
  const [pwOpen, setPwOpen]           = React.useState(false);
  const [pwCurrent, setPwCurrent]     = React.useState('');
  const [pwNew, setPwNew]             = React.useState('');
  const [pwConfirm, setPwConfirm]     = React.useState('');
  const [pwMsg, setPwMsg]             = React.useState('');
  const [pwSaving, setPwSaving]       = React.useState(false);
  // Notificações
  const [notifOpen, setNotifOpen]       = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [unreadCount, setUnreadCount]   = React.useState(0);
  const [notifLoading, setNotifLoading] = React.useState(false);
  const [solPendCount, setSolPendCount] = React.useState(0);
  const menuRef        = React.useRef(null);
  const gestaoRef      = React.useRef(null);
  const notifRef       = React.useRef(null);
  const handleLogoutRef  = React.useRef(null);
  const lastActivityRef  = React.useRef(Date.now()); // último evento de interação
  const renewingRef      = React.useRef(false);       // evita chamadas simultâneas

  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isLogs = location.pathname === '/logs';
  const isFirewall = location.pathname === '/firewall';
  const isServer = location.pathname === '/server';
  const isCluster = location.pathname === '/cluster';
  const isBackups = location.pathname === '/backups';
  const isIncidentes = location.pathname === '/incidentes';
  const isEventos = location.pathname === '/eventos';
  const isClients = location.pathname === '/clients';
  const isFinanceiro = location.pathname === '/financeiro';
  const isUsuarios = location.pathname === '/usuarios';
  const isMeusUsuarios = location.pathname === '/meus-usuarios';
  const isNotificacoes = location.pathname === '/notificacoes';
  const isSolicitacoes = location.pathname === '/solicitacoes';
  const isTelegram = location.pathname === '/telegram';
  const isGestaoActive = isLogs || isFirewall || isServer || isCluster || isBackups || isIncidentes || isEventos || isClients || isFinanceiro || isUsuarios || isMeusUsuarios || isNotificacoes || isSolicitacoes || isTelegram;

  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get('/api/vms');
        const vms = res.data?.data || [];
        setVmStats({
          online:  vms.filter(v => v.status === 'running').length,
          offline: vms.filter(v => v.status !== 'running').length,
        });
      } catch (_) {}
    };
    fetchStats();
    const t = setInterval(fetchStats, 30000);
    return () => clearInterval(t);
  }, []);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (gestaoRef.current && !gestaoRef.current.contains(e.target)) setGestaoOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Rastreia atividade do usuário para decisão de renovação automática
  React.useEffect(() => {
    const onActivity = () => { lastActivityRef.current = Date.now(); };
    document.addEventListener('mousemove', onActivity);
    document.addEventListener('click', onActivity);
    document.addEventListener('keydown', onActivity);
    return () => {
      document.removeEventListener('mousemove', onActivity);
      document.removeEventListener('click', onActivity);
      document.removeEventListener('keydown', onActivity);
    };
  }, []);

  const renewSession = React.useCallback(async () => {
    if (renewingRef.current) return;
    renewingRef.current = true;
    try {
      const res = await axios.post('/api/auth/session');
      if (res.data.expiresAt) {
        localStorage.setItem('sessionExpiry', String(res.data.expiresAt));
      }
    } catch (_) {
      // falhou silenciosamente — countdown continua normalmente
    } finally {
      renewingRef.current = false;
    }
  }, []);

  // Countdown da sessão — atualiza a cada segundo e faz logout ao expirar
  // Renova automaticamente se o usuário esteve ativo nos últimos 5 min e restam ≤10 min
  React.useEffect(() => {
    const RENEW_THRESHOLD = 10 * 60;      // renova quando restar ≤10 min
    const ACTIVITY_WINDOW = 5 * 60 * 1000; // usuário considerado ativo se interagiu nos últimos 5 min

    const tick = () => {
      const expiry = parseInt(localStorage.getItem('sessionExpiry') || '0', 10);
      const left = Math.max(0, Math.floor((expiry - Date.now()) / 1000));
      setSessionLeft(left);

      if (left === 0) {
        if (handleLogoutRef.current) handleLogoutRef.current();
        return;
      }

      // Auto-renova se estiver no limiar de aviso E usuário esteve ativo recentemente
      if (left <= RENEW_THRESHOLD && left > 0) {
        const recentActivity = Date.now() - lastActivityRef.current < ACTIVITY_WINDOW;
        if (recentActivity && !renewingRef.current) {
          renewSession();
        }
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [renewSession]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Polling de notificações (badge) ──────────────────────────
  React.useEffect(() => {
    const fetchCount = () => {
      axios.get('/api/notifications/unread-count')
        .then(r => setUnreadCount(r.data.unreadCount || 0))
        .catch(() => {});
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    window.addEventListener('notificationsUpdated', fetchCount);
    return () => { clearInterval(t); window.removeEventListener('notificationsUpdated', fetchCount); };
  }, []);

  // ── Polling de solicitações pendentes (badge, só admin) ──────
  React.useEffect(() => {
    if (!(isLogAdmin || isFirewallAdmin)) return;
    const fetchSolCount = () => {
      axios.get('/api/admin/solicitacoes/pendentes-count')
        .then(r => setSolPendCount(r.data.count || 0))
        .catch(() => {});
    };
    fetchSolCount();
    const t = setInterval(fetchSolCount, 60000);
    return () => clearInterval(t);
  }, [isLogAdmin, isFirewallAdmin]);

  const fetchNotifications = async () => {
    setNotifLoading(true);
    try {
      const res = await axios.get('/api/notifications');
      setNotifications(res.data.data || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (_) {}
    setNotifLoading(false);
  };

  const toggleNotif = () => {
    if (!notifOpen) fetchNotifications();
    setNotifOpen(o => !o);
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.patch(`/api/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch (_) {}
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post('/api/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnreadCount(0);
    } catch (_) {}
  };

  const fetchTotpStatus = async () => {
    try {
      const res = await axios.get('/api/user/2fa');
      setTotpStatus(res.data);
    } catch (_) { setTotpStatus({ enabled: false, pending: false, setupAt: null }); }
  };

  const startTotpSetup = async () => {
    setTotpSaving(true); setTotpMsg('');
    try {
      const res = await axios.post('/api/user/2fa/setup');
      setTotpSetupData({ qrCode: res.data.qrCode, manualCode: res.data.manualCode });
      setTotpConfirm('');
    } catch (e) {
      setTotpMsg({ type: 'error', text: e.response?.data?.error || 'Erro ao iniciar 2FA' });
    } finally { setTotpSaving(false); }
  };

  const confirmTotpSetup = async () => {
    if (totpConfirm.length < 6) return;
    setTotpSaving(true); setTotpMsg('');
    try {
      await axios.post('/api/user/2fa/confirm', { code: totpConfirm });
      setTotpMsg({ type: 'success', text: '2FA ativado com sucesso!' });
      setTotpSetupData(null); setTotpConfirm('');
      fetchTotpStatus();
    } catch (e) {
      setTotpMsg({ type: 'error', text: e.response?.data?.error || 'Código inválido.' });
      setTotpConfirm('');
    } finally {
      setTotpSaving(false);
      setTimeout(() => setTotpMsg(''), 4000);
    }
  };

  const disableTotp = async () => {
    setTotpSaving(true); setTotpMsg('');
    try {
      await axios.delete('/api/user/2fa');
      setTotpMsg({ type: 'success', text: '2FA desativado.' });
      setTotpSetupData(null); setTotpConfirm('');
      fetchTotpStatus();
    } catch (e) {
      setTotpMsg({ type: 'error', text: e.response?.data?.error || 'Erro ao desativar.' });
    } finally {
      setTotpSaving(false);
      setTimeout(() => setTotpMsg(''), 3000);
    }
  };

  const resetPwForm = () => { setPwOpen(false); setPwCurrent(''); setPwNew(''); setPwConfirm(''); setPwMsg(''); };

  const changePassword = async () => {
    if (!pwCurrent || !pwNew || !pwConfirm) {
      setPwMsg({ type: 'error', text: 'Preencha todos os campos.' }); return;
    }
    if (pwNew !== pwConfirm) {
      setPwMsg({ type: 'error', text: 'As novas senhas não coincidem.' }); return;
    }
    if (pwNew.length < 8) {
      setPwMsg({ type: 'error', text: 'A nova senha deve ter no mínimo 8 caracteres.' }); return;
    }
    setPwSaving(true); setPwMsg('');
    try {
      await axios.put('/api/user/password', { currentPassword: pwCurrent, newPassword: pwNew });
      setPwMsg({ type: 'success', text: 'Senha alterada com sucesso!' });
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
      setTimeout(() => { setPwMsg(''); setPwOpen(false); }, 3000);
    } catch (e) {
      setPwMsg({ type: 'error', text: e.response?.data?.error || 'Erro ao alterar senha.' });
    } finally {
      setPwSaving(false);
    }
  };

  const toggleMenu = () => {
    if (!menuOpen) { fetchTotpStatus(); setTotpSetupData(null); setTotpConfirm(''); setTotpMsg(''); resetPwForm(); }
    setMenuOpen(o => !o);
  };

  const handleLogout = async () => {
    try { await axios.post('/api/auth/logout'); } catch (_) {}
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('sessionExpiry');
    if (setIsLogAdmin) setIsLogAdmin(false);
    if (setIsFirewallAdmin) setIsFirewallAdmin(false);
    if (setIsRevenda) setIsRevenda(false);
    if (setAuthUser) setAuthUser(null);
    if (setDisplayUser) setDisplayUser(null);
    setIsAuthenticated(false);
  };
  handleLogoutRef.current = handleLogout;

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <button className="navbar-logo" onClick={() => navigate('/')} title="Ir para Home">
          <LogoFull height={40} theme={theme} />
        </button>
        {vmStats !== null && (
          <div className="vm-stats">
            <button className="vm-stat-btn online" onClick={() => navigate('/?status=online')} title="Ver VMs online">
              <span className="vm-stat-dot" />{vmStats.online} Online
            </button>
            <button className={`vm-stat-btn offline${vmStats.offline > 0 ? ' has-offline' : ''}`} onClick={() => navigate('/?status=offline')} title="Ver VMs offline">
              <span className="vm-stat-dot" />{vmStats.offline} Offline
            </button>
          </div>
        )}
        <div className="navbar-user">
          {!isHome && (
            <button className="home-btn" onClick={() => navigate('/')} title="Voltar para lista de VMs">
              🏠 Home
            </button>
          )}
          {/* Notificações (sino) */}
          <div className="notif-wrap" ref={notifRef}>
            <button className="notif-bell-btn" onClick={toggleNotif} title="Notificações">
              🔔
              {unreadCount > 0 && <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
            </button>
            {notifOpen && (
              <div className="notif-dropdown">
                <div className="notif-dropdown-header">
                  <span className="notif-dropdown-title">Notificações</span>
                  {unreadCount > 0 && (
                    <button className="notif-mark-all-btn" onClick={handleMarkAllRead}>Marcar todas como lidas</button>
                  )}
                </div>
                <div className="notif-dropdown-list">
                  {notifLoading ? (
                    <div className="notif-empty">Carregando...</div>
                  ) : notifications.length === 0 ? (
                    <div className="notif-empty">Nenhuma notificação</div>
                  ) : (
                    notifications.map(n => (
                      <button
                        key={n.id}
                        className={`notif-item${!n.read_at ? ' unread' : ''}`}
                        onClick={() => { if (!n.read_at) handleMarkRead(n.id); }}
                      >
                        <span className="notif-item-icon">{n.type === 'financeiro' ? '💰' : '✨'}</span>
                        <div className="notif-item-body">
                          <div className="notif-item-title">{n.title}</div>
                          {n.message && <div className="notif-item-msg">{n.message.length > 120 ? n.message.slice(0, 120) + '...' : n.message}</div>}
                          <div className="notif-item-date">{new Date(n.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                        {!n.read_at && <span className="notif-item-dot" />}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {(isLogAdmin || isFirewallAdmin || isRevenda) && (() => {
            const hasClusterAlerts = nodeAlerts && Object.values(nodeAlerts).some(n => n.hasAlerts);
            const isAdmin = isLogAdmin || isFirewallAdmin;
            return (
              <div className="gestao-menu-wrap" ref={gestaoRef}>
                <button className={`logs-nav-btn${isGestaoActive ? ' active' : ''}${gestaoOpen ? ' open' : ''}`}
                        onClick={() => setGestaoOpen(o => !o)}>
                  ⚙ Gestão <span className="user-menu-chevron">{gestaoOpen ? '▲' : '▼'}</span>
                  {nodeAlerts && isAdmin && <span className={`cluster-alert-dot${hasClusterAlerts ? ' has-alerts' : ''}`} />}
                </button>
                {gestaoOpen && (
                  <div className="gestao-dropdown">
                    {isLogAdmin && <button className={isLogs ? 'active' : ''} onClick={() => { navigate('/logs'); setGestaoOpen(false); }}>📋 Logs</button>}
                    {isFirewallAdmin && <button className={isFirewall ? 'active' : ''} onClick={() => { navigate('/firewall'); setGestaoOpen(false); }}>🛡️ Firewall</button>}
                    {isAdmin && <button className={isServer ? 'active' : ''} onClick={() => { navigate('/server'); setGestaoOpen(false); }}>🖥 Server</button>}
                    {isAdmin && <button className={isCluster ? 'active' : ''} onClick={() => { navigate('/cluster'); setGestaoOpen(false); }}>🌐 Cluster</button>}
                    {isAdmin && <button className={isBackups ? 'active' : ''} onClick={() => { navigate('/backups'); setGestaoOpen(false); }}>💾 Backups</button>}
                    {isAdmin && <button className={isIncidentes ? 'active' : ''} onClick={() => { navigate('/incidentes'); setGestaoOpen(false); }}>🚨 Incidentes</button>}
                    {isAdmin && <button className={isEventos ? 'active' : ''} onClick={() => { navigate('/eventos'); setGestaoOpen(false); }}>📈 Eventos</button>}
                    <button className={isClients ? 'active' : ''} onClick={() => { navigate('/clients'); setGestaoOpen(false); }}>👥 Clientes</button>
                    <button className={isFinanceiro ? 'active' : ''} onClick={() => { navigate('/financeiro'); setGestaoOpen(false); }}>💰 Financeiro</button>
                    {isAdmin && <button className={isUsuarios ? 'active' : ''} onClick={() => { navigate('/usuarios'); setGestaoOpen(false); }}>👤 Usuários</button>}
                    {isAdmin && <button className={isNotificacoes ? 'active' : ''} onClick={() => { navigate('/notificacoes'); setGestaoOpen(false); }}>🔔 Notificações</button>}
                    {isAdmin && (
                      <button className={isSolicitacoes ? 'active' : ''} onClick={() => { navigate('/solicitacoes'); setGestaoOpen(false); }}>
                        📨 Solicitações{solPendCount > 0 && <span className="notif-badge" style={{ position: 'static', marginLeft: '0.4rem' }}>{solPendCount > 99 ? '99+' : solPendCount}</span>}
                      </button>
                    )}
                    {isAdmin && <button className={isTelegram ? 'active' : ''} onClick={() => { navigate('/telegram'); setGestaoOpen(false); }}>✈️ Telegram</button>}
                    {isRevenda && <button className={isMeusUsuarios ? 'active' : ''} onClick={() => { navigate('/meus-usuarios'); setGestaoOpen(false); }}>👥 Meus Usuários</button>}
                  </div>
                )}
              </div>
            );
          })()}

          {/* User menu */}
          {displayUser && (
            <div className="user-menu-wrap" ref={menuRef}>
              <button className={`user-info-btn${menuOpen ? ' open' : ''}`} onClick={toggleMenu} title="Configurações do usuário">
                👤 {displayUser}
                <span className="user-menu-chevron">{menuOpen ? '▲' : '▼'}</span>
              </button>

              {menuOpen && (
                <div className={`user-dropdown${totpSetupData ? ' user-dropdown-wide' : ''}`}>
                  <div className="user-dropdown-header">
                    <span className="user-dropdown-icon">👤</span>
                    <div>
                      <div className="user-dropdown-name">{displayUser}</div>
                    </div>
                  </div>

                  <div className="user-dropdown-divider" />

                  <div className="user-dropdown-section">
                    <div className="user-dropdown-section-title">🔐 Autenticação em dois fatores</div>

                    {totpStatus === null ? (
                      <div className="user-dropdown-loading">Carregando...</div>
                    ) : (
                      <>
                        <div className="user-2fa-status-row">
                          {totpStatus.enabled ? (
                            <>
                              <span className="totp-status-badge totp-badge-enabled">✓ Ativo</span>
                              {totpStatus.setupAt && (
                                <span className="user-2fa-date">desde {new Date(totpStatus.setupAt).toLocaleDateString('pt-BR')}</span>
                              )}
                            </>
                          ) : totpStatus.pending ? (
                            <span className="totp-status-badge totp-badge-pending">⏳ Pendente</span>
                          ) : (
                            <span className="user-2fa-off">Não configurado</span>
                          )}
                        </div>

                        {!totpSetupData && (
                          <div className="user-2fa-btns">
                            {!totpStatus.enabled && (
                              <button className="user-2fa-setup-btn" onClick={startTotpSetup} disabled={totpSaving}>
                                {totpSaving ? '...' : totpStatus.pending ? '🔄 Reconfigurar' : '+ Ativar 2FA'}
                              </button>
                            )}
                            {(totpStatus.enabled || totpStatus.pending) && (
                              <button className="user-2fa-disable-btn" onClick={disableTotp} disabled={totpSaving}>
                                {totpSaving ? '...' : '✕ Desativar'}
                              </button>
                            )}
                          </div>
                        )}

                        {totpSetupData && (
                          <div className="user-2fa-setup">
                            <img src={totpSetupData.qrCode} alt="QR Code 2FA" className="user-2fa-qr" />
                            <div className="user-2fa-steps">
                              <div className="user-2fa-step">1. Abra o <strong>Google Authenticator</strong></div>
                              <div className="user-2fa-step">2. Escaneie o QR ou use a chave manual:</div>
                              <div className="user-2fa-manual">{totpSetupData.manualCode}</div>
                              <div className="user-2fa-step">3. Digite o código gerado:</div>
                              <div className="user-2fa-confirm-row">
                                <input
                                  className="user-2fa-input"
                                  type="text"
                                  inputMode="numeric"
                                  placeholder="000000"
                                  value={totpConfirm}
                                  onChange={e => setTotpConfirm(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                  onKeyDown={e => { if (e.key === 'Enter') confirmTotpSetup(); }}
                                  maxLength={6}
                                  autoComplete="one-time-code"
                                  disabled={totpSaving}
                                  autoFocus
                                />
                                <button className="user-2fa-confirm-btn" onClick={confirmTotpSetup} disabled={totpSaving || totpConfirm.length < 6}>
                                  {totpSaving ? '...' : '✓ Ativar'}
                                </button>
                              </div>
                              <button className="user-2fa-cancel" onClick={() => { setTotpSetupData(null); setTotpConfirm(''); }} disabled={totpSaving}>
                                Cancelar
                              </button>
                            </div>
                          </div>
                        )}

                        {totpMsg && (
                          <div className={`user-2fa-msg user-2fa-msg-${totpMsg.type}`}>{totpMsg.text}</div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="user-dropdown-divider" />

                  {/* Troca de senha */}
                  <div className="user-dropdown-section">
                    <button
                      className={`user-pw-toggle${pwOpen ? ' open' : ''}`}
                      onClick={() => { setPwOpen(o => !o); if (pwOpen) resetPwForm(); }}
                    >
                      🔑 Alterar senha
                      <span className="user-menu-chevron">{pwOpen ? '▲' : '▼'}</span>
                    </button>

                    {pwOpen && (
                      <div className="user-pw-form">
                        <input
                          className="user-pw-input"
                          type="password"
                          placeholder="Senha atual"
                          value={pwCurrent}
                          onChange={e => setPwCurrent(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') changePassword(); }}
                          disabled={pwSaving}
                          autoComplete="current-password"
                          autoFocus
                        />
                        <input
                          className="user-pw-input"
                          type="password"
                          placeholder="Nova senha (mín. 8 caracteres)"
                          value={pwNew}
                          onChange={e => setPwNew(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') changePassword(); }}
                          disabled={pwSaving}
                          autoComplete="new-password"
                        />
                        <input
                          className="user-pw-input"
                          type="password"
                          placeholder="Confirmar nova senha"
                          value={pwConfirm}
                          onChange={e => setPwConfirm(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') changePassword(); }}
                          disabled={pwSaving}
                          autoComplete="new-password"
                        />
                        <div className="user-pw-actions">
                          <button
                            className="user-pw-cancel"
                            onClick={resetPwForm}
                            disabled={pwSaving}
                          >Cancelar</button>
                          <button
                            className="user-pw-confirm"
                            onClick={changePassword}
                            disabled={pwSaving || !pwCurrent || !pwNew || !pwConfirm}
                          >{pwSaving ? '...' : '✓ Alterar'}</button>
                        </div>
                        {pwMsg && (
                          <div className={`user-pw-msg user-pw-msg-${pwMsg.type}`}>{pwMsg.text}</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="user-dropdown-divider" />

                  {/* Meus Dados */}
                  <button
                    className="user-dropdown-nav-btn"
                    onClick={() => { navigate('/profile'); setMenuOpen(false); }}
                  >
                    📋 Meus Dados
                  </button>
                </div>
              )}
            </div>
          )}

          <button className="theme-toggle" onClick={toggleTheme} title="Alternar tema">
            {theme === 'dark' ? '☀️ Claro' : '🌙 Escuro'}
          </button>
          {sessionLeft !== null && (
            <div
              className={`session-timer${sessionLeft <= 300 ? ' session-danger' : sessionLeft <= 600 ? ' session-warn' : ''}`}
              title="Tempo restante de sessão"
            >
              ⏱ {formatSessionTime(sessionLeft)}
              {sessionLeft <= 600 && (
                <button
                  className="session-renew-btn"
                  onClick={renewSession}
                  title="Renovar sessão"
                >
                  ⟳
                </button>
              )}
            </div>
          )}
          <button className="logout-btn" onClick={handleLogout}>⬅️ Sair</button>
        </div>
      </div>
    </nav>
  );
}

export default App;
